import React, { useState, useEffect, useRef } from 'react';
import { Network, Server, Wifi, Home, Globe, Building, AlertTriangle, Map, Activity, Layers, Shield, Clock } from 'lucide-react';

const TracerouteVisualization = ({ results }) => {
  const [activeHop, setActiveHop] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [completedHops, setCompletedHops] = useState([]);
  const [packetPositions, setPacketPositions] = useState([]);
  const [showDetails, setShowDetails] = useState(null);
  const [viewMode, setViewMode] = useState('linear'); // 'linear', 'map', 'globe'
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  // Process and enhance the results data
  const enhancedResults = useRef(null);
  useEffect(() => {
    if (!results?.hops?.length) return;
    
    // Process and enhance the data
    enhancedResults.current = {
      ...results,
      hops: results.hops.map((hop, index) => {
        // Add additional metadata for visualization
        const networkType = determineNetworkType(hop, index, results.hops.length);
        const securityLevel = determineSecurityLevel(hop);
        const connectionType = determineConnectionType(hop, index, results.hops.length);
        
        return {
          ...hop,
          networkType,
          securityLevel,
          connectionType,
          // Calculate x/y positions for non-linear views
          xPos: Math.cos(index / results.hops.length * Math.PI * 2) * 40 + 50,
          yPos: Math.sin(index / results.hops.length * Math.PI * 2) * 40 + 50,
          // More sophisticated data analysis
          anomaly: detectAnomaly(hop, index, results.hops),
          jitter: calculateJitter(hop, index, results.hops),
          packetLoss: calculatePacketLoss(hop),
        };
      })
    };
    
    // Auto-start animation
    startAnimation();
  }, [results]);

  // Helpers to determine network characteristics
  const determineNetworkType = (hop, index, totalHops) => {
    if (index === 0) return 'local';
    if (index === totalHops - 1) return 'destination';
    if (hop.hostname?.includes('router')) return 'router';
    if (hop.hostname?.includes('gateway') || hop.hostname?.includes('gw')) return 'gateway';
    if (hop.ip_address?.startsWith('10.') || hop.ip_address?.startsWith('192.168.')) return 'private';
    if (index < totalHops * 0.3) return 'isp';
    if (index > totalHops * 0.7) return 'destination-network';
    return 'backbone';
  };
  
  const determineSecurityLevel = (hop) => {
    // Simulate security level based on hop characteristics
    if (hop.hostname?.includes('secure') || hop.hostname?.includes('ssl')) return 'high';
    if (hop.hostname?.includes('vpn') || hop.hostname?.includes('firewall')) return 'very-high';
    // Random distribution of security levels for visualization
    const rand = Math.random();
    if (rand > 0.7) return 'high';
    if (rand > 0.4) return 'medium';
    return 'standard';
  };
  
  const determineConnectionType = (hop, index, totalHops) => {
    if (index === 0) return 'ethernet';
    if (index === totalHops - 1) return 'server';
    if (hop.hostname?.includes('fiber')) return 'fiber';
    if (hop.hostname?.includes('satellite')) return 'satellite';
    if (hop.latency > 100) return 'congested';
    if (hop.latency < 10) return 'high-speed';
    return 'standard';
  };
  
  const detectAnomaly = (hop, index, hops) => {
    if (!hop.latency) return false;
    if (index > 0 && hop.latency > hops[index-1].latency * 3) return 'latency-spike';
    if (hop.latency > 150) return 'high-latency';
    return false;
  };
  
  const calculateJitter = (hop, index, hops) => {
    if (index === 0 || !hop.latency || !hops[index-1].latency) return 0;
    return Math.abs(hop.latency - hops[index-1].latency);
  };
  
  const calculatePacketLoss = (hop) => {
    // Simulate packet loss (would be real data in production)
    if (!hop.latency) return 100; // No response = 100% loss
    if (hop.latency > 100) return Math.min(30, hop.latency / 10);
    return Math.random() * 2; // Small random value for visualization
  };

  // Animation functions
  const startAnimation = () => {
    setIsAnimating(true);
    setActiveHop(0);
    setCompletedHops([]);
    setPacketPositions([]);
    
    // Clear any existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    // Start the animation sequence
    animateNextHop(0);
  };
  
  const animateNextHop = (hopIndex) => {
    if (!enhancedResults.current?.hops || hopIndex >= enhancedResults.current.hops.length) {
      setIsAnimating(false);
      return;
    }
    
    const currentHop = enhancedResults.current.hops[hopIndex];
    setActiveHop(hopIndex);
    
    // Generate multiple data packets for visual effect
    const packetCount = Math.max(1, Math.min(5, Math.floor(Math.random() * 5) + 1));
    const newPackets = Array(packetCount).fill(0).map((_, i) => ({
      id: `packet-${hopIndex}-${i}`,
      startHop: hopIndex === 0 ? 0 : hopIndex - 1,
      targetHop: hopIndex,
      progress: 0,
      type: i === 0 ? 'primary' : 'secondary',
      speed: 3 + Math.random() * 2,
      size: i === 0 ? 10 : 6 + Math.random() * 4,
    }));
    
    setPacketPositions(prev => [...prev, ...newPackets]);
    
    // Animate the packets
    const animatePackets = () => {
      setPacketPositions(prev => {
        const updated = prev.map(packet => {
          if (packet.startHop === hopIndex - 1 && packet.targetHop === hopIndex) {
            const newProgress = packet.progress + packet.speed;
            if (newProgress >= 100) {
              // Packet arrived
              if (packet.type === 'primary') {
                // Primary packet reached destination, proceed to next hop
                timeoutRef.current = setTimeout(() => {
                  setCompletedHops(prev => [...prev, hopIndex]);
                  const delay = currentHop.latency ? Math.min(currentHop.latency * 20, 2000) : 500;
                  timeoutRef.current = setTimeout(() => animateNextHop(hopIndex + 1), delay);
                }, 300);
              }
              return null; // Remove this packet
            }
            return { ...packet, progress: newProgress };
          }
          return packet;
        }).filter(Boolean);
        
        return updated;
      });
    };
    
    // Run the animation at 60fps
    const interval = setInterval(animatePackets, 16);
    
    // Clean up interval after animation is complete
    timeoutRef.current = setTimeout(() => {
      clearInterval(interval);
    }, 3000);
  };

  // Visualizations
  const getHopIcon = (hop) => {
    switch (hop.networkType) {
      case 'local': return <Home />;
      case 'router': return <Wifi />;
      case 'gateway': return <Building />;
      case 'destination': return <Server />;
      case 'backbone': return <Layers />;
      default: return <Globe />;
    }
  };
  
  const getHopColor = (hop) => {
    switch (hop.networkType) {
      case 'local': return { bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500' };
      case 'router': return { bg: 'bg-purple-500', text: 'text-purple-500', border: 'border-purple-500' };
      case 'gateway': return { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500' };
      case 'backbone': return { bg: 'bg-indigo-500', text: 'text-indigo-500', border: 'border-indigo-500' };
      case 'isp': return { bg: 'bg-cyan-500', text: 'text-cyan-500', border: 'border-cyan-500' };
      case 'destination': return { bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500' };
      case 'destination-network': return { bg: 'bg-emerald-500', text: 'text-emerald-500', border: 'border-emerald-500' };
      case 'private': return { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500' };
      default: return { bg: 'bg-gray-500', text: 'text-gray-500', border: 'border-gray-500' };
    }
  };
  
  const getLatencyColor = (latency) => {
    if (!latency) return 'bg-gray-600';
    if (latency < 5) return 'bg-green-400';
    if (latency < 20) return 'bg-emerald-400';
    if (latency < 50) return 'bg-blue-400';
    if (latency < 100) return 'bg-yellow-400';
    if (latency < 200) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getLatencyGradient = (hop1, hop2) => {
    const color1 = getLatencyColor(hop1?.latency || 0).replace('bg-', '');
    const color2 = getLatencyColor(hop2?.latency || 0).replace('bg-', '');
    return `bg-gradient-to-r from-${color1} to-${color2}`;
  };

  // Determine what to render
  if (!enhancedResults.current || !enhancedResults.current.hops?.length) {
    return (
      <div className="bg-gray-900 p-8 rounded-xl text-center">
        <Network className="h-16 w-16 text-blue-500 mx-auto mb-4" />
        <h2 className="text-xl text-white font-bold">Waiting for traceroute data...</h2>
      </div>
    );
  }
  
  // Render different view modes
  const renderLinearView = () => (
    <div className="relative mb-12">
      {/* Background track */}
      <div className="absolute w-full h-3 bg-gray-800 top-24 rounded-full overflow-hidden">
        {/* Completed progress bar */}
        <div 
          className="h-full bg-gradient-to-r from-blue-600 via-purple-600 to-green-600"
          style={{ 
            width: `${(Math.max(0, completedHops.length - 1) / (enhancedResults.current.hops.length - 1)) * 100}%`,
            transition: 'width 0.5s ease'
          }}
        />
      </div>
  
      {/* Nodes */}
      <div className="flex justify-between w-full relative">
        {enhancedResults.current.hops.map((hop, index) => {
          const hopColors = getHopColor(hop);
          const isCompleted = completedHops.includes(index);
          const isActive = activeHop === index;
          const statusClass = isCompleted 
            ? `${hopColors.bg} shadow-lg shadow-${hopColors.bg.replace('bg-', '')}/50` 
            : 'bg-gray-800';
          
          return (
            <div 
              key={hop.hop_number} 
              className="flex flex-col items-center z-10"
              onClick={() => setShowDetails(showDetails === index ? null : index)}
            >
              {/* Node */}
              <div 
                className={`w-12 h-12 rounded-full flex items-center justify-center ${statusClass} 
                  transition-all duration-500 cursor-pointer
                  ${isActive ? 'scale-125' : ''} ${isCompleted ? '' : 'opacity-70'}`}
              >
                {React.cloneElement(getHopIcon(hop), { 
                  className: `h-6 w-6 ${isCompleted ? 'text-white' : hopColors.text}` 
                })}
                
                {/* Anomaly indicator */}
                {hop.anomaly && (
                  <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-4 h-4 flex items-center justify-center">
                    <AlertTriangle className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
              
              {/* Label */}
              <div className={`mt-2 text-center transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-70'}`}>
                <p className={`text-xs font-semibold ${isCompleted ? 'text-white' : 'text-gray-400'}`}>
                  {hop.hop_number}
                </p>
              </div>
              
              {/* Latency badge */}
              {isCompleted && (
                <div className={`mt-1 px-2 py-0.5 rounded-full ${getLatencyColor(hop.latency)} text-xs font-bold`}>
                  {hop.latency || '?'}ms
                </div>
              )}
            </div>
          );
        })}
      </div>
  
      {/* Animated data packets */}
      {packetPositions.map(packet => {
        if (packet.startHop >= enhancedResults.current.hops.length - 1) return null;
        
        const startNode = packet.startHop;
        const endNode = packet.targetHop;
        const progress = packet.progress;
        
        // Calculate position along the path
        const totalNodes = enhancedResults.current.hops.length;
        const segmentWidth = 100 / (totalNodes - 1);
        const startPos = segmentWidth * startNode;
        const endPos = segmentWidth * endNode;
        const currentPos = startPos + (progress / 100) * (endPos - startPos);
        
        return (
          <div 
            key={packet.id}
            className={`absolute top-24 transform -translate-y-1/2 transition-all duration-50 ease-linear
              ${packet.type === 'primary' ? 'z-30' : 'z-20'}`}
            style={{ 
              left: `${currentPos}%`,
              marginLeft: -packet.size / 2,
              marginTop: packet.type === 'primary' ? -1 : (Math.random() * 6) - 3
            }}
          >
            <div 
              className={`rounded-full animate-pulse 
                ${packet.type === 'primary' ? 'bg-blue-500 shadow-lg shadow-blue-500/50' : 'bg-blue-400/70'}`}
              style={{ 
                width: `${packet.size}px`, 
                height: `${packet.size}px`,
              }}
            >
              {packet.type === 'primary' && (
                <div className="w-1/2 h-1/2 bg-white rounded-full absolute top-1/4 left-1/4"></div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderMapView = () => (
    <div className="relative h-96 mb-8 bg-gray-800 rounded-xl overflow-hidden">
      {/* Simulated map background */}
      <div className="absolute inset-0 opacity-20">
        <svg width="100%" height="100%" viewBox="0 0 100 100">
          <path d="M10,30 Q30,5 50,30 T90,30" fill="none" stroke="#4B5563" strokeWidth="0.5" />
          <path d="M10,70 Q30,45 50,70 T90,70" fill="none" stroke="#4B5563" strokeWidth="0.5" />
          <path d="M30,10 Q5,30 30,50 T30,90" fill="none" stroke="#4B5563" strokeWidth="0.5" />
          <path d="M70,10 Q45,30 70,50 T70,90" fill="none" stroke="#4B5563" strokeWidth="0.5" />
          {/* Grid lines */}
          {Array(10).fill(0).map((_, i) => (
            <React.Fragment key={i}>
              <line x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="#4B5563" strokeWidth="0.2" />
              <line x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="#4B5563" strokeWidth="0.2" />
            </React.Fragment>
          ))}
        </svg>
      </div>
      
      {/* Network nodes */}
      {enhancedResults.current.hops.map((hop, index) => {
        const hopColors = getHopColor(hop);
        const isCompleted = completedHops.includes(index);
        const isActive = activeHop === index;
        const statusClass = isCompleted 
          ? `${hopColors.bg} shadow-lg shadow-${hopColors.bg.replace('bg-', '')}/50` 
          : 'bg-gray-800';
        
        return (
          <div 
            key={hop.hop_number}
            className={`absolute transition-all duration-300`}
            style={{ 
              left: `${hop.xPos}%`, 
              top: `${hop.yPos}%`,
              transform: `translate(-50%, -50%) scale(${isActive ? 1.25 : 1})`,
              zIndex: isActive ? 20 : 10
            }}
            onClick={() => setShowDetails(showDetails === index ? null : index)}
          >
            {/* Connection lines to previous node */}
            {index > 0 && (
              <div 
                className={`absolute ${isCompleted && completedHops.includes(index - 1) ? 'bg-blue-500/50' : 'bg-gray-700'} transition-colors duration-500`}
                style={{
                  height: '2px',
                  width: `${Math.sqrt(
                    Math.pow(hop.xPos - enhancedResults.current.hops[index - 1].xPos, 2) +
                    Math.pow(hop.yPos - enhancedResults.current.hops[index - 1].yPos, 2)
                  )}%`,
                  transformOrigin: '0 0',
                  transform: `rotate(${Math.atan2(
                    hop.yPos - enhancedResults.current.hops[index - 1].yPos,
                    hop.xPos - enhancedResults.current.hops[index - 1].xPos
                  )}rad)`,
                  left: 0,
                  top: 0,
                  zIndex: -1,
                  opacity: 0.7
                }}
              />
            )}
            
            {/* Node */}
            <div 
              className={`w-12 h-12 rounded-full flex items-center justify-center ${statusClass} border-2
                ${isCompleted ? 'border-white/30' : 'border-gray-700'} cursor-pointer`}
            >
              {React.cloneElement(getHopIcon(hop), { 
                className: `h-6 w-6 ${isCompleted ? 'text-white' : hopColors.text}` 
              })}
              
              {/* Anomaly indicator */}
              {hop.anomaly && (
                <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-4 h-4 flex items-center justify-center">
                  <AlertTriangle className="h-3 w-3 text-white" />
                </div>
              )}
            </div>
            
            {/* Label */}
            <div className={`absolute top-full mt-1 left-1/2 -translate-x-1/2 text-center transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-70'}`}>
              <p className={`text-xs font-semibold px-2 py-0.5 rounded bg-gray-900/80 ${isCompleted ? 'text-white' : 'text-gray-400'}`}>
                {hop.hop_number}
              </p>
            </div>
            
            {/* Latency badge */}
            {isCompleted && (
              <div className={`absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full ${getLatencyColor(hop.latency)} text-xs font-bold`}>
                {hop.latency || '?'}ms
              </div>
            )}
          </div>
        );
      })}
      
      {/* Animated data packets in map view */}
      {packetPositions.map(packet => {
        if (packet.startHop >= enhancedResults.current.hops.length - 1) return null;
        
        const startNode = enhancedResults.current.hops[packet.startHop];
        const endNode = enhancedResults.current.hops[packet.targetHop];
        const progress = packet.progress / 100;
        
        // Calculate position using linear interpolation
        const currentX = startNode.xPos + (endNode.xPos - startNode.xPos) * progress;
        const currentY = startNode.yPos + (endNode.yPos - startNode.yPos) * progress;
        
        return (
          <div 
            key={packet.id}
            className={`absolute transition-all duration-50 ease-linear
              ${packet.type === 'primary' ? 'z-30' : 'z-20'}`}
            style={{ 
              left: `${currentX}%`,
              top: `${currentY}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div 
              className={`rounded-full animate-pulse 
                ${packet.type === 'primary' ? 'bg-blue-500 shadow-lg shadow-blue-500/50' : 'bg-blue-400/70'}`}
              style={{ 
                width: `${packet.size}px`, 
                height: `${packet.size}px`,
              }}
            >
              {packet.type === 'primary' && (
                <div className="w-1/2 h-1/2 bg-white rounded-full absolute top-1/4 left-1/4"></div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Current hop details panel
  const renderHopDetails = () => {
    if (activeHop >= enhancedResults.current.hops.length) return null;
    
    const hop = enhancedResults.current.hops[activeHop];
    const isCompleted = completedHops.includes(activeHop);
    const hopColors = getHopColor(hop);
    
    return (
      <div className={`bg-gray-800 bg-opacity-90 p-6 rounded-xl border ${hopColors.border} shadow-lg transition-all duration-300 mb-6`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${hopColors.bg} bg-opacity-20`}>
              {React.cloneElement(getHopIcon(hop), { className: hopColors.text })}
            </div>
            
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-xl font-bold text-white">
                  {hop.hostname || `Hop ${hop.hop_number}`}
                </h3>
                {hop.securityLevel === 'high' && (
                  <Shield className="h-4 w-4 text-green-400" />
                )}
                {hop.securityLevel === 'very-high' && (
                  <div className="flex">
                    <Shield className="h-4 w-4 text-green-400" />
                    <Shield className="h-4 w-4 text-green-400 -ml-2" />
                  </div>
                )}
              </div>
              
              <p className="text-gray-400">
                {hop.ip_address} • {getNetworkTypeLabel(hop.networkType)}
              </p>
              
              {hop.location && (
                <p className="text-gray-400 mt-1">
                  <Map className="h-4 w-4 inline mr-1" />
                  {hop.location}
                </p>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            {isCompleted && (
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getLatencyColor(hop.latency)} bg-opacity-90`}>
                <Clock className="h-4 w-4" />
                <span className="font-bold">{hop.latency || '?'}ms</span>
              </div>
            )}
            
            {isCompleted && hop.jitter > 5 && (
              <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-orange-400 bg-opacity-80 text-sm">
                <Activity className="h-4 w-4" />
                <span>Jitter: {hop.jitter.toFixed(1)}ms</span>
              </div>
            )}
            
            {hop.anomaly && (
              <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-red-500 bg-opacity-80 text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>{formatAnomaly(hop.anomaly)}</span>
              </div>
            )}
          </div>
        </div>
        
        {isCompleted && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-900 bg-opacity-70 p-3 rounded-md">
              <p className="text-xs text-gray-400">Latency</p>
              <p className="text-lg font-bold text-white">{hop.latency || '?'}ms</p>
              {activeHop > 0 && (
                <p className="text-xs text-gray-400">
                  {hop.latency > enhancedResults.current.hops[activeHop - 1].latency ? '+' : ''}
                  {hop.latency - enhancedResults.current.hops[activeHop - 1].latency}ms from previous
                </p>
              )}
            </div>
            
            <div className="bg-gray-900 bg-opacity-70 p-3 rounded-md">
              <p className="text-xs text-gray-400">Packet Loss</p>
              <p className="text-lg font-bold text-white">{hop.packetLoss.toFixed(1)}%</p>
              <div className="w-full bg-gray-700 h-1 mt-1 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${hop.packetLoss > 5 ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(100, hop.packetLoss)}%` }}
                />
              </div>
            </div>
            
            <div className="bg-gray-900 bg-opacity-70 p-3 rounded-md">
              <p className="text-xs text-gray-400">Connection</p>
              <p className="text-lg font-bold text-white">{formatConnectionType(hop.connectionType)}</p>
              <p className="text-xs text-gray-400">{getNetworkTypeLabel(hop.networkType)}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper formatters
  const getNetworkTypeLabel = (type) => {
    switch (type) {
      case 'local': return 'Local Network';
      case 'router': return 'Network Router';
      case 'gateway': return 'Network Gateway';
      case 'backbone': return 'Internet Backbone';
      case 'isp': return 'Internet Service Provider';
      case 'destination': return 'Destination Server';
      case 'destination-network': return 'Destination Network';
      case 'private': return 'Private Network';
      default: return 'Internet Node';
    }
  };
  
  const formatAnomaly = (anomaly) => {
    switch (anomaly) {
      case 'latency-spike': return 'Latency Spike';
      case 'high-latency': return 'High Latency';
      default: return 'Anomaly Detected';
    }
  };
  
  const formatConnectionType = (type) => {
    switch (type) {
      case 'ethernet': return 'Ethernet';
      case 'fiber': return 'Fiber Optic';
      case 'satellite': return 'Satellite';
      case 'congested': return 'Congested';
      case 'high-speed': return 'High-Speed';
      case 'server': return 'Server Connection';
      default: return 'Standard';
    }
  };

  return (
    <div className="bg-gray-900 p-6 rounded-xl shadow-xl" ref={containerRef}>
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Network className="text-blue-400" size={36} />
          <div>
            <h1 className="text-2xl font-bold text-white">Network Path to {results.target}</h1>
            <p className="text-gray-400">
              {enhancedResults.current.hops.length} hops • {
                completedHops.length === enhancedResults.current.hops.length 
                  ? 'Complete' 
                  : isAnimating 
                    ? 'Tracing' 
                    : 'Paused'
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex space-x-2 bg-gray-800 p-1 rounded-lg">
            <button 
              onClick={() => setViewMode('linear')}
              className={`px-3 py-1 rounded ${viewMode === 'linear' ? 'bg-blue-500 text-white' : 'text-gray-400'}`}
            >
              Linear
            </button>
            <button 
              onClick={() => setViewMode('map')}
              className={`px-3 py-1 rounded ${viewMode === 'map' ? 'bg-blue-500 text-white' : 'text-gray-400'}`}
            >
              Map
            </button>
          </div>
          
          <button 
            onClick={() => isAnimating ? setIsAnimating(false) : startAnimation()}
            className={`px-3 py-1 rounded ${isAnimating ? 'bg-amber-500' : 'bg-green-500'} text-white`}
          >
            {isAnimating ? 'Pause' : 'Start'}
          </button>
        </div>
      </div>
      
      {/* Current hop details */}
      {renderHopDetails()}
      
      {/* Main visualization */}
      {viewMode === 'linear' ? renderLinearView() : renderMapView()}
      
      {/* Legend */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <div className="bg-gray-800 p-3 rounded-lg">
          <h3 className="text-gray-300 text-sm font-semibold mb-2">Network Types</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-xs text-gray-400">Local Network</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-xs text-gray-400">Gateway</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-xs text-gray-400">Router</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xs text-gray-400">Destination</span>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 p-3 rounded-lg">
          <h3 className="text-gray-300 text-sm font-semibold mb-2">Latency</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
              <span className="text-xs text-gray-400">&lt;5ms (Excellent)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-400"></div>
              <span className="text-xs text-gray-400">5-50ms (Good)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <span className="text-xs text-gray-400">50-100ms (Fair)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <span className="text-xs text-gray-400">&gt;100ms (Poor)</span>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 p-3 rounded-lg">
          <h3 className="text-gray-300 text-sm font-semibold mb-2">Status</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-3 w-3 text-red-500" />
              <span className="text-xs text-gray-400">Anomaly Detected</span>
            </div>
            <div className="flex items-center space-x-2">
              <Activity className="h-3 w-3 text-orange-400" />
              <span className="text-xs text-gray-400">High Jitter</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="h-3 w-3 text-green-400" />
              <span className="text-xs text-gray-400">Secure Connection</span>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 p-3 rounded-lg">
          <h3 className="text-gray-300 text-sm font-semibold mb-2">Statistics</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Total Hops:</span>
              <span className="text-xs text-white font-mono">{enhancedResults.current.hops.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Average Latency:</span>
              <span className="text-xs text-white font-mono">
                {completedHops.length > 0 
                  ? (enhancedResults.current.hops
                      .filter((h, i) => completedHops.includes(i) && h.latency)
                      .reduce((sum, h) => sum + h.latency, 0) / 
                      enhancedResults.current.hops.filter((h, i) => completedHops.includes(i) && h.latency).length
                    ).toFixed(1)
                  : '?'
                } ms
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Total Time:</span>
              <span className="text-xs text-white font-mono">
                {completedHops.length > 0
                  ? enhancedResults.current.hops
                      .filter((h, i) => completedHops.includes(i) && h.latency)
                      .reduce((sum, h) => sum + h.latency, 0).toFixed(1)
                  : '?'
                } ms
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Detailed node information (when clicked) */}
      {showDetails !== null && showDetails < enhancedResults.current.hops.length && (
        <div className="mt-8 bg-gray-800 p-6 rounded-xl border border-gray-700 shadow-xl">
          <div className="flex justify-between">
            <h2 className="text-xl font-bold text-white mb-4">
              Hop {enhancedResults.current.hops[showDetails].hop_number} Details
            </h2>
            <button 
              onClick={() => setShowDetails(null)}
              className="text-gray-400 hover:text-white"
            >
              &times;
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-1 text-gray-400">IP Address</td>
                    <td className="py-1 text-white font-mono">{enhancedResults.current.hops[showDetails].ip_address || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-400">Hostname</td>
                    <td className="py-1 text-white">{enhancedResults.current.hops[showDetails].hostname || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-400">Network Type</td>
                    <td className="py-1 text-white">{getNetworkTypeLabel(enhancedResults.current.hops[showDetails].networkType)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-400">Connection Type</td>
                    <td className="py-1 text-white">{formatConnectionType(enhancedResults.current.hops[showDetails].connectionType)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div>
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-1 text-gray-400">Latency</td>
                    <td className="py-1 text-white font-mono">{enhancedResults.current.hops[showDetails].latency || '?'} ms</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-400">Jitter</td>
                    <td className="py-1 text-white font-mono">{enhancedResults.current.hops[showDetails].jitter?.toFixed(1) || '?'} ms</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-400">Packet Loss</td>
                    <td className="py-1 text-white font-mono">{enhancedResults.current.hops[showDetails].packetLoss?.toFixed(1) || '?'}%</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-400">Security Level</td>
                    <td className="py-1 text-white">{enhancedResults.current.hops[showDetails].securityLevel?.charAt(0).toUpperCase() + enhancedResults.current.hops[showDetails].securityLevel?.slice(1) || 'Standard'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Anomalies section */}
          {enhancedResults.current.hops[showDetails].anomaly && (
            <div className="mt-4 p-4 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg">
              <h3 className="text-red-400 font-semibold flex items-center">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Anomaly Detected
              </h3>
              <p className="text-gray-300 text-sm mt-1">
                {enhancedResults.current.hops[showDetails].anomaly === 'latency-spike' 
                  ? 'This hop shows a significant latency increase compared to previous hops, which may indicate network congestion or routing issues.'
                  : enhancedResults.current.hops[showDetails].anomaly === 'high-latency'
                    ? 'This hop has unusually high latency which may impact overall connection performance.'
                    : 'An anomaly was detected at this hop which may require investigation.'
                }
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Summary statistics panel (shown when complete) */}
      {completedHops.length === enhancedResults.current.hops.length && (
        <div className="mt-8 bg-gradient-to-r from-blue-900 to-indigo-900 p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Activity className="mr-2" />
            Traceroute Complete
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-black bg-opacity-20 p-4 rounded-lg">
              <h3 className="text-blue-300 font-semibold text-sm">Path Summary</h3>
              <p className="text-lg font-bold text-white">{enhancedResults.current.hops.length} Hops</p>
              <p className="text-sm text-gray-300">From {enhancedResults.current.source} to {enhancedResults.current.target}</p>
            </div>
            
            <div className="bg-black bg-opacity-20 p-4 rounded-lg">
              <h3 className="text-blue-300 font-semibold text-sm">Performance</h3>
              <div className="flex items-center space-x-4">
                <div>
                  <p className="text-lg font-bold text-white">
                    {enhancedResults.current.hops
                      .filter(h => h.latency)
                      .reduce((sum, h) => sum + h.latency, 0).toFixed(1)} ms
                  </p>
                  <p className="text-xs text-gray-300">Total Latency</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-white">
                    {(enhancedResults.current.hops
                      .filter(h => h.latency)
                      .reduce((sum, h) => sum + h.latency, 0) / 
                      enhancedResults.current.hops.filter(h => h.latency).length).toFixed(1)} ms
                  </p>
                  <p className="text-xs text-gray-300">Average Latency</p>
                </div>
              </div>
            </div>
            
            <div className="bg-black bg-opacity-20 p-4 rounded-lg">
              <h3 className="text-blue-300 font-semibold text-sm">Issues</h3>
              <p className="text-lg font-bold text-white">
                {enhancedResults.current.hops.filter(h => h.anomaly).length} Anomalies
              </p>
              <p className="text-sm text-gray-300">
                {enhancedResults.current.hops.filter(h => h.latency > 100).length} High Latency Hops
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TracerouteVisualization;